/* eslint-disable import/extensions */
/** @module Auth0CookieHelper */
import logger from './logging/auth0-logger.mjs';

const SECONDS_IN_DAY = 86400;
const ACTUAL_MAX_COOKIE_SIZE_BYTES = 4095;
const MAX_COOKIE_SIZE_BYTES = 3800;
const SPLIT_COOKIE_FRAGMENT_NUMBER_TOKEN = '{num}';
const SPLIT_COOKIE_BASE_NAME_TOKEN = '{baseName}';
const SPLIT_COOKIE_MARKER_TOKEN = '_@@sc_';
const SPLIT_COOKIE_MANIFEST_PROP_NAME = `manifest_${SPLIT_COOKIE_MARKER_TOKEN}`;
const SPLIT_COOKIE_TEMPLATE = `${SPLIT_COOKIE_BASE_NAME_TOKEN}${SPLIT_COOKIE_MARKER_TOKEN}${SPLIT_COOKIE_FRAGMENT_NUMBER_TOKEN}`;

class Auth0CookieHelper {
  static getExpirationFromDays(days) {
    return this.getExpirationFromSeconds(days * SECONDS_IN_DAY);
  }

  static getExpirationFromSeconds(seconds) {
    const now = new Date();
    let future = new Date();
    if (seconds > 0) {
      future.setTime(now.getTime() + (seconds * 1000));
    } else {
      future = new Date('January 1, 1970 00:00:01 GMT');
    }
    return future.toUTCString();
  }

  static get auth0CookieDomain() {
    let domain = window.location.hostname;
    const hostNameParts = domain.split('.');
    if (hostNameParts.length >= 2) {
      domain = `domain=.${
        hostNameParts[hostNameParts.length - 2]
      }.${
        hostNameParts[hostNameParts.length - 1]}`;
    } else {
      domain = ''; // domain must be removed for localhost
    }
    return domain.toLowerCase();
  }

  static setCookie(name, val, cookieOptions) {
    const stringVal = this._getCookieValueString(val);
    const maxFragmentSizeBytes = cookieOptions.maxFragmentSizeBytesOverride ?? MAX_COOKIE_SIZE_BYTES;
    if (stringVal.length > maxFragmentSizeBytes && cookieOptions.allowSplitCookie) {
      this._writeSplitCookie(name, stringVal, cookieOptions);
      return;
    }
    this._setCookie(name, stringVal, cookieOptions);
  }

  // vanilla set cookie (normal, non-split)
  static _setCookie(name, val, cookieOptions) {
    const domain = cookieOptions.domain || this.auth0CookieDomain;
    const { expiry } = cookieOptions;
    const auth0Cookie = `${name}=${val}; expires=${expiry}; path=/; ${domain}; Secure; SameSite=None`;

    if (auth0Cookie.length > ACTUAL_MAX_COOKIE_SIZE_BYTES) {
      throw new Error(`Cookie "${name}" is too big (${auth0Cookie.length} bytes > ${ACTUAL_MAX_COOKIE_SIZE_BYTES} bytes)`);
    }
    document.cookie = auth0Cookie;
    console.log(`Auth0CookieHelper.setCookie => (${auth0Cookie.length} bytes) ${auth0Cookie}`);

    if (val && !document.cookie.includes(`${name}=`)) { // setting cookie, make sure it exists
      throw new Error(`Cookie set for ${name} has failed! Are cookies disabled?`);
    }
  }

  static _getCookieValueString = (val) => {
    if (!val) return '';
    return encodeURIComponent(typeof val === 'string'
      ? val
      : JSON.stringify(val));
  };

  static _writeSplitCookie = (name, val, cookieOptions) => {
    let valTraversalIndex = 0;
    let fragmentCounter = 0;
    const maxFragmentSizeBytes = cookieOptions.maxFragmentSizeBytesOverride ?? MAX_COOKIE_SIZE_BYTES;
    console.log(`Auth0CookieHelper._writeSplitCookie => start using max fragment size ${maxFragmentSizeBytes} bytes...`);
    const manifest = [];

    const getNextFragment = () => {
      const frag = val.substring(valTraversalIndex, valTraversalIndex + maxFragmentSizeBytes);
      valTraversalIndex += maxFragmentSizeBytes;
      return frag;
    };
    const generateNextFragmentCookieName = () => {
      fragmentCounter++;
      return SPLIT_COOKIE_TEMPLATE
        .replace(SPLIT_COOKIE_BASE_NAME_TOKEN, name)
        .replace(SPLIT_COOKIE_FRAGMENT_NUMBER_TOKEN, fragmentCounter);
    };

    let fragmentValue = getNextFragment();
    do {
      const fragmentName = generateNextFragmentCookieName();
      this._setCookie(fragmentName, fragmentValue, cookieOptions);
      manifest.push(fragmentName);
      fragmentValue = getNextFragment();
    } while (fragmentValue);

    const manifestObj = {};
    manifestObj[SPLIT_COOKIE_MANIFEST_PROP_NAME] = manifest;
    this._setCookie(name, JSON.stringify(manifestObj), cookieOptions);
  };

  static setCookieValueWithExpirationInSeconds(name, val, expiryInSeconds) {
    this.setCookie(name, val, { expiry: this.getExpirationFromSeconds(expiryInSeconds) });
  }

  static setCookieValueWithExpirationInMinutes(name, val, expiryInMinutes) {
    this.setCookie(name, val, { expiry: this.getExpirationFromSeconds(expiryInMinutes * 60) });
  }

  static setCookieValueWithExpirationInDays(name, val, expiryInDays) {
    this.setCookie(name, val, { expiry: this.getExpirationFromDays(expiryInDays) });
  }

  static setCookieValueForever(name, val) {
    this.setCookie(name, val, { expiry: this.getExpirationFromDays(365) });
  }

  static duplicateCookie(srcCookieName, newCookieName, cookieOptions) {
    const srcVal = this.getCookieValue(srcCookieName);
    if (!srcVal) {
      throw new Error(`Could not duplicate cookie ${srcCookieName} because it does not exist`);
    }
    this.setCookie(newCookieName, srcVal, cookieOptions);
  }

  static duplicateCookieValueWithExpirationInSeconds(srcCookieName, newCookieName, expiryInSeconds) {
    this.duplicateCookie(srcCookieName, newCookieName, { expiry: this.getExpirationFromSeconds(expiryInSeconds) });
  }

  static duplicateCookieValueWithExpirationInMinutes(srcCookieName, newCookieName, expiryInMinutes) {
    this.duplicateCookie(srcCookieName, newCookieName, { expiry: this.getExpirationFromSeconds(expiryInMinutes * 60) });
  }

  static duplicateCookieValueWithExpirationInDays(srcCookieName, newCookieName, expiryInDays) {
    this.duplicateCookie(srcCookieName, newCookieName, { expiry: this.getExpirationFromDays(expiryInDays) });
  }

  static duplicateCookieValueForever(srcCookieName, newCookieName) {
    this.duplicateCookie(srcCookieName, newCookieName, { expiry: this.getExpirationFromDays(365) });
  }

  static getAllCookiesAsDictionary() {
    const cookies = document.cookie.split('; ');
    const cookieDict = {};

    cookies.forEach((cookie) => {
      const [name, value] = cookie.split('=');
      cookieDict[decodeURIComponent(name)] = decodeURIComponent(value);
    });

    return cookieDict;
  }

  static getAllCookiesAsArray() {
    const cookies = document.cookie.split('; ');
    const cookieArray = [];

    cookies.forEach((cookie) => {
      const separatorIndex = cookie.indexOf('=');
      if (separatorIndex !== -1) {
        const name = cookie.substring(0, separatorIndex);
        const value = cookie.substring(separatorIndex + 1); // everything after the first =
        cookieArray.push({ name, value });
      }
    });

    return cookieArray;
  }


  static getMatchingCookies(pattern) {
    const all = this.getAllCookiesAsArray();
    return all.filter((cookieObj) => {
      return (pattern instanceof RegExp && cookieObj.name.match(pattern))
             || (typeof pattern === 'string' && cookieObj.name.contains(pattern));
    });
  }

  static escapeRegex = (str) => {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  static getCookieValue(name, options = {}) {
    console.log(`Auth0CookieHelper.getCookieValue named => ${name}`);
    const escapedName = this.escapeRegex(name);
    const valMatch = document.cookie.match(`(^|;)\\s*${escapedName}\\s*=\\s*([^;]+)`);
    if (valMatch === null) {
      return '';
    }
    const foundCookieVal = options.skipUriDecode ? valMatch.pop() : decodeURIComponent(valMatch.pop());
    // maybe a split cookie?
    const splitCookieManifest = this._getSplitCookieManifestArray(foundCookieVal);
    return splitCookieManifest.length
      ? this._getSplitCookieValue(splitCookieManifest)
      : foundCookieVal;
  }

  static _getSplitCookieManifestArray = (splitCookieManifest) => {
    // const maybeSplitCookieManifest = this.getCookieValue(name);

    try {
      const maybeManifestObj = JSON.parse(splitCookieManifest);
      return this._hasProp(maybeManifestObj, SPLIT_COOKIE_MANIFEST_PROP_NAME)
        ? maybeManifestObj[SPLIT_COOKIE_MANIFEST_PROP_NAME]
        : [];
    } catch (error) {
      return [];
    }
  };

  static _getSplitCookieValue(cookiePartsManifest) {
    console.log(`Auth0CookieHelper._getSplitCookieValue with manifest => ${JSON.stringify(cookiePartsManifest)}...`);
    const getCookieOptions = {
      // cookie fragments may be split across single encoded values and will fail decode alone
      // perform the decode only after reassembly...
      skipUriDecode: true,
    };
    const reassembledEncodedVal = cookiePartsManifest.map(cookieName => this.getCookieValue(cookieName, getCookieOptions)).join('');
    const decodedVal = decodeURIComponent(reassembledEncodedVal);
    console.log(`Auth0CookieHelper._getSplitCookieValue built => ${decodedVal}`);
    return decodedVal;
  }

  static deleteCookies(cookieNameList) {
    if (!cookieNameList || !Array.isArray(cookieNameList)) {
      throw new Error('Cannot delete cookies, cookieNameList must be an array of cookie names');
    }

    cookieNameList.forEach((c) => {
      try {
        this.deleteCookie(c);
      } catch (error) {
        logger.warn(`Unable to delete cookie "${c}"`, error);
      }
    });
  }

  static deleteCookie(name) {
    console.log(`Auth0CookieHelper.deleteCookie => ${name}`);
    const escapedName = this.escapeRegex(name);
    const valMatch = document.cookie.match(`(^|;)\\s*${escapedName}\\s*=\\s*([^;]+)`);
    if (valMatch === null) {
      console.log(`Auth0CookieHelper.deleteCookie => no cookie named ${name} was found`);
      return;
    }
    const foundCookieVal = valMatch.pop();
    // maybe a split cookie?
    const splitCookieManifest = this._getSplitCookieManifestArray(foundCookieVal);
    if (!splitCookieManifest.length) {
      console.log(`Auth0CookieHelper.deleteCookie => ${name} is a NOT a split cookie...`);
      this._deleteCookie(name);
      return;
    }
    console.log(`Auth0CookieHelper.deleteCookie => ${name} is a split cookie...`);
    this._deleteSplitCookie(splitCookieManifest);
    // then delete manifest itself
    this._deleteCookie(name);
  }

  static _deleteSplitCookie(cookiePartsManifest) {
    cookiePartsManifest.forEach(partName => this._deleteCookie(partName));
  }

  static _deleteCookie(name) {
    this.setCookie(name, '', { expiry: this.getExpirationFromDays(0) });
    // first try wildcard domain delete
    if (this.getCookieValue(name)) {
      // then subdomain specific domain
      this.setCookie(name, '', { expiry: this.getExpirationFromDays(0), domain: window.location.hostname });
      if (this.getCookieValue(name)) {
        logger.warn(`WARNING: Attempt to deleted cookie named ${name} has failed!`);
      }
    }
  }

  static _hasProp = (obj, prop) => {
    return !!(obj && Object.prototype.hasOwnProperty.call(obj, prop) && obj[prop]);
  };

  static get browserCookiesEnabled() {
    try {
      this._setCookie('ss.cookie.interrogate', '1', { expiry: this.getExpirationFromSeconds(30) });
    } catch (error) {
      return false;
    }
    return true;
  }

  static promoteCookiesToTLD(cookieNames) {
    const domainParts = window.location.hostname.split('.');
    if (domainParts.length < 2) {
      console.warn('Cannot promote cookies: not on a subdomain.');
      return;
    }

    const tldDomain = `.${domainParts.slice(-2).join('.')}`;
    const now = new Date();
    const cookies = document.cookie.split('; ');

    cookieNames.forEach((name) => {
      const found = cookies.find(c => c.startsWith(`${name}=`));
      if (!found) return;

      const value = found.substring(name.length + 1);

      // Remove the cookie from the current domain/subdomain
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;

      // Re-add the cookie on the top-level domain
      // Note: Setting 1-year expiry arbitrarily (since we can't read original expiration)
      const expires = new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000).toUTCString();
      document.cookie = `${name}=${value}; path=/; domain=${tldDomain}; expires=${expires}`;

      console.log(`Promoted cookie "${name}" to domain ${tldDomain}`);
    });
  }
}

export default Auth0CookieHelper;
